/**
 * Dashboard experience config (SYR-79).
 *
 * A single global flag, `marketerViewDefault`, decides whether org users land
 * on the new Marketer View shell at `/dashboard` (classic dashboard parked at
 * `/old-dashboard`) or on the classic dashboard at `/dashboard` (Marketer View
 * at `/marketer-view`). The value comes from the public config endpoint so the
 * route tree is stable before the user profile loads.
 *
 * Consumers should use the route-root hooks rather than hardcoding paths:
 *   useMarketerRoot() → '/dashboard' | '/marketer-view'
 *   useClassicRoot()  → '/old-dashboard' | '/dashboard'
 *
 * so the same components work at either mount point and flipping the flag is a
 * pure data change (no redeploy).
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useGetDashboardConfigQuery } from '../services/nestjs-base-api';

interface DashboardConfig {
  /** Marketer View is the default dashboard (lands at /dashboard). */
  marketerViewDefault: boolean;
  /** Customer-facing API surface (API Keys tab, Integrations tab) is live. */
  apiAccessEnabled: boolean;
  /** Config resolved (public endpoint returned or failed). */
  ready: boolean;
}

const DashboardConfigContext = createContext<DashboardConfig>({
  marketerViewDefault: false,
  apiAccessEnabled: false,
  ready: false,
});

export const DashboardConfigProvider = ({ children }: { children: ReactNode }) => {
  const { data, isError } = useGetDashboardConfigQuery();

  // Treat a failed config fetch as "classic default" so the app still routes.
  const ready = data !== undefined || isError;
  const value: DashboardConfig = {
    marketerViewDefault: data?.marketerViewDefault ?? false,
    apiAccessEnabled: data?.apiAccessEnabled ?? false,
    ready,
  };

  return (
    <DashboardConfigContext.Provider value={value}>
      {children}
    </DashboardConfigContext.Provider>
  );
};

export const useDashboardConfig = (): DashboardConfig =>
  useContext(DashboardConfigContext);

/** True when the Marketer View shell is the default dashboard. */
export const useMarketerViewDefault = (): boolean =>
  useContext(DashboardConfigContext).marketerViewDefault;

/** True when the customer-facing API surface (keys, integrations) is live. */
export const useApiAccessEnabled = (): boolean =>
  useContext(DashboardConfigContext).apiAccessEnabled;

/** Route root for the Marketer View shell (brand hash is appended by callers). */
export const useMarketerRoot = (): string =>
  useMarketerViewDefault() ? '/dashboard' : '/marketer-view';

/** Route root for the classic dashboard shell. */
export const useClassicRoot = (): string =>
  useMarketerViewDefault() ? '/old-dashboard' : '/dashboard';
